import { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Wind, Pause, Play, RotateCcw } from 'lucide-react';

const PHASES = [
  { name: 'Breathe In', duration: 4, scale: 1.35 },
  { name: 'Hold', duration: 4, scale: 1.35 },
  { name: 'Breathe Out', duration: 6, scale: 1 },
  { name: 'Rest', duration: 2, scale: 1 }
];

const TOTAL_CYCLE = PHASES.reduce((s, p) => s + p.duration, 0);

export const BreathingWidget = () => {
  const [active, setActive] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [cycles, setCycles] = useState(0);
  const raf = useRef(null);
  const start = useRef(null);
  const paused = useRef(0);

  useEffect(() => {
    if (!active) { cancelAnimationFrame(raf.current); return; }
    start.current = performance.now() - paused.current * 1000;
    const tick = (now) => {
      const secs = (now - start.current) / 1000;
      setElapsed(secs);
      setCycles(Math.floor(secs / TOTAL_CYCLE));
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [active]);

  const reset = () => { setActive(false); setElapsed(0); setCycles(0); paused.current = 0; };
  const toggle = () => {
    if (active) { paused.current = elapsed; }
    setActive(!active);
  };

  const cycleTime = elapsed % TOTAL_CYCLE;
  let acc = 0, phaseIdx = 0;
  for (let i = 0; i < PHASES.length; i++) {
    if (cycleTime < acc + PHASES[i].duration) { phaseIdx = i; break; }
    acc += PHASES[i].duration;
  }

  const phase = PHASES[phaseIdx];
  const phaseProgress = (cycleTime - acc) / phase.duration;

  const prevScale = phaseIdx === 0 ? PHASES[PHASES.length - 1].scale : PHASES[phaseIdx - 1].scale;
  const targetScale = phase.scale;
  const currentScale = prevScale + (targetScale - prevScale) * phaseProgress;

  const countdown = Math.ceil(phase.duration - (cycleTime - acc));

  return (
    <Card className="rounded-2xl border-white/10 overflow-hidden" data-testid="breathing-widget">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Wind className="h-4 w-4 text-cyan-600" />
            <span className="text-sm font-bold text-white">Breathing Exercise</span>
          </div>
          <span className="text-xs text-smoke tabular-nums">{cycles} cycle{cycles !== 1 ? 's' : ''}</span>
        </div>

        {/* Circle */}
        <div className="flex items-center justify-center py-6">
          <div className="relative w-36 h-36 flex items-center justify-center">
            {/* Outer ring */}
            <div
              className="absolute inset-0 rounded-full border-2 border-blue-200"
              style={{ transform: `scale(${active ? currentScale : 1})`, transition: active ? 'none' : 'transform 0.5s ease' }}
            />
            {/* Pulse ring */}
            <div
              className="absolute inset-0 rounded-full border border-cyan-200 animate-pulse-ring"
              style={{ display: active ? 'block' : 'none' }}
            />
            {/* Inner glow */}
            <div
              className="absolute rounded-full bg-gradient-to-br from-blue-50 to-cyan-50"
              style={{
                width: '80%', height: '80%',
                transform: `scale(${active ? currentScale : 1})`,
                transition: active ? 'none' : 'transform 0.5s ease'
              }}
            />
            {/* Center content */}
            <div className="relative text-center z-10">
              <p className="text-sm font-bold text-white mb-0.5">
                {active ? phase.name : 'Ready'}
              </p>
              {active && (
                <p className="text-2xl font-extrabold text-white tabular-nums">{countdown}</p>
              )}
            </div>
          </div>
        </div>

        {/* Phase dots */}
        <div className="flex justify-center gap-1.5 mb-5">
          {PHASES.map((p, i) => (
            <div
              key={p.name}
              className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                i === phaseIdx && active ? 'bg-slate-600' : 'bg-white/10'
              }`}
            />
          ))}
        </div>

        {/* Controls */}
        <div className="flex justify-center gap-2">
          <Button
            variant={active ? "outline" : "default"}
            size="sm"
            className={`rounded-full h-9 px-5 ${!active ? 'bg-slate-800 hover:bg-slate-900' : 'border-white/10'}`}
            onClick={toggle}
            data-testid="breathing-toggle-btn"
          >
            {active ? <Pause className="h-3.5 w-3.5 mr-1.5" /> : <Play className="h-3.5 w-3.5 mr-1.5" />}
            {active ? 'Pause' : 'Start'}
          </Button>
          {elapsed > 0 && (
            <Button variant="ghost" size="sm" className="rounded-full h-9 text-smoke" onClick={reset} data-testid="breathing-reset-btn">
              <RotateCcw className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
